import {
    Box,
    Button,
    Card,
    Form,
    FormButtonRow,
    FormGroup,
    FormParagraph,
    IconButton,
    PageHeader,
    Select,
} from '@ksyos/design-system';
import { useState } from 'react';
import type { IAdminProcess } from '@common/interfaces';
import { ManagerAuthorization, ProcessStatus, ProcessTaskStatus, ToastType } from '@common/enums';
import { booleanAsJaNee } from '@common/utils/textUtils';
import { NullableFormattedDatetime } from '@frontend/components/elements/NullableFormattedDatetime';
import { ProcessAdminStatus } from '@frontend/components/elements/processStatus/ProcessAdminStatus';
import { PractitionerAndOrganization } from '@frontend/components/pages/processes/newAdminProcessDetails/PractitionerAndOrganization';
import { UpdateProcessDetailsModal } from '@frontend/components/pages/processes/newAdminProcessDetails/UpdateProcessDetailsModal';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { updateProcess } from '@frontend/services/api/processesApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { showToast } from '@frontend/utils/showToast';

type ProcessInfoSectionProps = {
    onShowCancelProcessModal: () => void;
    onShowWorkflowModal: () => void;
    process: IAdminProcess;
    reloadProcess: () => void;
};

export function ProcessInfoSection({
    process,
    onShowCancelProcessModal,
    onShowWorkflowModal,
    reloadProcess,
}: Readonly<ProcessInfoSectionProps>) {
    const translate = getSelectedLanguage();

    assertHasAuthorization(ManagerAuthorization.adminProcessRead);
    const canEditStatus = hasOneAuthorization(ManagerAuthorization.updateProcessWorkflowStepsAndActivities);

    function shouldShowCancelProcessButton(processStatus: ProcessStatus): boolean {
        return processStatus === ProcessStatus.active && hasOneAuthorization(ManagerAuthorization.adminProcessEdit);
    }

    const abortSignal = useAbortSignal();
    const [isUpdateProcessDetailsModalOpen, setIsUpdateProcessDetailsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [processStatus, setProcessStatus] = useState(process.status);

    const tasks = process.tasks.filter(
        (processTask) =>
            processTask.status === ProcessTaskStatus.open || processTask.status === ProcessTaskStatus.inProgress
    );

    return (
        <>
            <PageHeader
                action={
                    <>
                        <Button onClick={() => onShowWorkflowModal()}>Bekijk workflow</Button>
                        {shouldShowCancelProcessButton(process.status) && (
                            <Button
                                onClick={() => onShowCancelProcessModal()}
                                variant="outline"
                            >
                                {getSelectedLanguage().common.cancelling}
                            </Button>
                        )}
                    </>
                }
                title={translate.request.processDetails}
            />
            <Card>
                <Form submitOnEnter={false}>
                    <Card.Transition type={isEditing ? 'edit' : 'read'} />
                    <Card.Header
                        action={
                            canEditStatus && !isEditing ? (
                                <IconButton
                                    icon="edit-light"
                                    labelText="edit"
                                    onClick={() => startEditing()}
                                />
                            ) : undefined
                        }
                        title={process.service.name}
                    />
                    <Card.Body>
                        <FormGroup label="Procesnummer (PID)">
                            <FormParagraph>{process.processNumber}</FormParagraph>
                        </FormGroup>
                        <FormGroup label="Status">
                            <FormParagraph>
                                {!isEditing ? (
                                    process.status
                                ) : (
                                    <Select
                                        onChange={(item) => {
                                            setProcessStatus(item?.value ?? processStatus);
                                        }}
                                        options={Object.values(ProcessStatus).map((status) => ({
                                            label: status,
                                            value: status,
                                        }))}
                                        placeholder="Status"
                                        required={true}
                                        value={{
                                            label: processStatus,
                                            value: processStatus,
                                        }}
                                    />
                                )}
                            </FormParagraph>
                        </FormGroup>
                        <FormGroup label="Gepauzeerd">
                            <FormParagraph>{booleanAsJaNee(process.isPaused)}</FormParagraph>
                        </FormGroup>
                        <FormGroup label="Acties">
                            {process.tasks.length === 0 ? (
                                <FormParagraph>Geen</FormParagraph>
                            ) : (
                                <Box
                                    display={'flex'}
                                    gap={4}
                                >
                                    {tasks.map((task) => (
                                        <ProcessAdminStatus
                                            key={task.id}
                                            status={process.status}
                                            taskStatus={task.status}
                                            taskType={task.type}
                                        />
                                    ))}
                                </Box>
                            )}
                        </FormGroup>
                        <FormGroup label="Gestart op">
                            <FormParagraph>
                                <NullableFormattedDatetime value={process.startedDatetime} />
                            </FormParagraph>
                        </FormGroup>
                        <PractitionerAndOrganization
                            label="Gestart door"
                            organization={process.startedByOrganization}
                            practitioner={process.startedByPractitioner}
                        />
                        <PractitionerAndOrganization
                            label="Aangevraagd door"
                            organization={process.requesterOrganization}
                            practitioner={process.requesterPractitioner}
                        />
                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={handleCancel}
                                />
                                <SaveButton
                                    disabled={isSaving || processStatus === process.status}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Card.Body>
                </Form>
            </Card>
            {isUpdateProcessDetailsModalOpen && (
                <UpdateProcessDetailsModal
                    currentProcessStatus={process.status}
                    onClose={() => setIsUpdateProcessDetailsModalOpen(false)}
                    onConfirm={update}
                />
            )}
        </>
    );

    async function startEditing() {
        setProcessStatus(process.status);
        setIsEditing(true);
    }

    async function handleCancel() {
        setProcessStatus(process.status);
        setIsEditing(false);
    }

    async function handleSubmit() {
        setIsUpdateProcessDetailsModalOpen(true);
    }

    async function update(): Promise<void> {
        setIsSaving(true);
        try {
            await updateProcess(process.id, { status: processStatus }, abortSignal);
            showToast(ToastType.success, getSelectedLanguage().processDetails.updateOk);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            showToast(ToastType.error, errorMessage);
        }
        setIsUpdateProcessDetailsModalOpen(false);
        reloadProcess();
        setIsEditing(false);
        setIsSaving(false);
    }
}
